import { useState } from "react";
import { ErrorCodes, ProblemError, hasGrant, usePrincipal, type Me } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import {
  ASSIGNABLE_ROLE_CODES,
  isElevatedRole,
  type AssignableRoleCode,
  type CreateStaffInput,
  type StaffCreatedSummary,
  type StaffSummary,
  type UpdateStaffInput,
} from "../../api/iam-contracts";
import type { MessageKey } from "../../i18n/dictionaries";
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Select,
  StatusLabel,
  Table,
  TableCard,
  Toast,
  ToastStack,
} from "../../ui";
import {
  useCreateStaff,
  useResetStaffPassword,
  useSetStaffStatus,
  useStaffList,
  useUpdateStaff,
} from "./use-iam";

// IAM / Ekip — the admin staff-provisioning + management surface. A create form
// (Ad Soyad + e-posta + rol → POST /v1/admin/staff, minting a Keycloak login +
// staff_profile + role and returning a ONE-TIME temporary password) PLUS the staff
// list + per-row edit (#216 backend): GET /v1/admin/staff (list), PATCH
// /v1/admin/staff/:id (rename / role change), POST .../status (enable-disable) and
// POST .../reset-password (one-time temp reveal). RBAC is UX: staff:write makes the
// create form visible, staff:read gates the list, and the elevated-role options +
// managing an ADMIN/SUPER_ADMIN target need staff.admin:write; the caller can never
// disable their own account. The API re-enforces every call regardless.

const ROLE_LABEL_KEY: Record<AssignableRoleCode, MessageKey> = {
  SUPER_ADMIN: "iam.role.SUPER_ADMIN",
  ADMIN: "iam.role.ADMIN",
  COMPLIANCE: "iam.role.COMPLIANCE",
  OPS: "iam.role.OPS",
  MANAGER: "iam.role.MANAGER",
  ADVISOR: "iam.role.ADVISOR",
  CLAIMS_COORDINATOR: "iam.role.CLAIMS_COORDINATOR",
  FINANCE: "iam.role.FINANCE",
  CONFIG_ADMIN: "iam.role.CONFIG_ADMIN",
  ANALYTICS: "iam.role.ANALYTICS",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Turkish/English role label for a (possibly unknown/unassigned) role code — falls
 *  back to the raw code, then an em dash. */
function roleLabel(t: (k: MessageKey) => string, code: string | null): string {
  if (!code) return "—";
  const key = ROLE_LABEL_KEY[code as AssignableRoleCode];
  return key ? t(key) : code;
}

/** Map a mutation ProblemError to a calm, typed message key (branch on the stable
 *  code/status, never on human text). */
function mutationErrorKey(err: unknown): MessageKey | null {
  if (!(err instanceof ProblemError)) return null;
  if (err.status === 503) return "iam.error.unavailable";
  if (err.is(ErrorCodes.FORBIDDEN)) return "iam.error.noPermission";
  if (err.is(ErrorCodes.NOT_FOUND)) return "iam.error.notFound";
  if (err.is(ErrorCodes.CONFLICT)) return "iam.error.conflict";
  return "iam.error.generic";
}

/** Best-effort self-match for the disable guard (the API re-enforces regardless):
 *  the principal's e-mail against the row's. Case-insensitive; null-safe. */
function isSelf(me: Me, row: StaffSummary): boolean {
  return !!me.email && !!row.email && me.email.toLowerCase() === row.email.toLowerCase();
}

export function Iam() {
  const { t } = useT();
  const { me } = usePrincipal();
  // staff.admin:write is SUPER_ADMIN-only: it gates ELEVATED-role creation (and cross-org).
  const canElevate = hasGrant(me, "staff.admin:write", "all");
  // staff:read@all gates the LIST read (ADMIN + SUPER_ADMIN hold it); the create form
  // stays visible on staff:write alone.
  const canRead = hasGrant(me, "staff:read", "all");
  const create = useCreateStaff();

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [roleCode, setRoleCode] = useState<AssignableRoleCode>("ADVISOR");
  const [created, setCreated] = useState<StaffCreatedSummary | null>(null);

  // Roles the caller may actually assign — the elevated pair is filtered out (as UX)
  // when the caller lacks staff.admin:write; the API re-enforces this regardless.
  const roleOptions = ASSIGNABLE_ROLE_CODES.filter((code) => canElevate || !isElevatedRole(code));

  const nameFilled = displayName.trim() !== "";
  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit = nameFilled && emailValid && !create.isPending;

  // Typed problem branches (branch on the stable `code`/`status`, never on human text).
  const err = create.error instanceof ProblemError ? create.error : null;
  const isDup = err?.is(ErrorCodes.CONFLICT) ?? false;
  const isForbidden = err?.is(ErrorCodes.FORBIDDEN) ?? false;
  const isUnavailable = err?.status === 503;
  const isValidation = err?.is(ErrorCodes.VALIDATION_FAILED) ?? false;

  function submit() {
    const input: CreateStaffInput = {
      displayName: displayName.trim(),
      email: email.trim(),
      roleCode,
    };
    create.mutate(input, {
      onSuccess: (summary) => {
        setCreated(summary);
        setDisplayName("");
        setEmail("");
        setRoleCode("ADVISOR");
      },
    });
  }

  function revealDone() {
    setCreated(null);
    create.reset();
  }

  return (
    <section className="surf-stack">
      <div className="surf-head">
        <div>
          <h1 className="surf-title">{t("iam.heading")}</h1>
          <p className="surf-sub muted">{t("iam.sub")}</p>
        </div>
      </div>

      <Card className="cat-formcard" data-testid="iam-form-card">
        <div className="surf-cardhead">
          <span className="surf-cardtitle">
            <Icon name="member" size={16} strokeWidth={1.9} /> {t("iam.create.title")}
          </span>
        </div>

        <form
          data-testid="iam-create-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) submit();
          }}
        >
          <fieldset className="k-form-grid" disabled={create.isPending}>
            <Input
              label={t("iam.field.displayName")}
              value={displayName}
              placeholder={t("iam.field.displayNamePlaceholder")}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="iam-displayName"
            />
            <Input
              label={t("iam.field.email")}
              type="email"
              value={email}
              placeholder="ad.soyad@ornek.com"
              onChange={(e) => setEmail(e.target.value)}
              error={isDup ? t("iam.error.dupEmail") : undefined}
              errorTestId="iam-email-error"
              data-testid="iam-email"
            />
            <Select
              label={t("iam.field.role")}
              value={roleCode}
              onChange={(e) => setRoleCode(e.target.value as AssignableRoleCode)}
              hint={canElevate ? undefined : t("iam.role.elevatedHidden")}
              data-testid="iam-role"
            >
              {roleOptions.map((code) => (
                <option key={code} value={code}>
                  {t(ROLE_LABEL_KEY[code])}
                </option>
              ))}
            </Select>
          </fieldset>

          <div className="k-form-actions">
            <Button
              variant="primary"
              type="submit"
              disabled={!canSubmit}
              loading={create.isPending}
              data-testid="iam-submit"
            >
              {t("iam.create.submit")}
            </Button>

            {isForbidden && (
              <span role="alert" className="form-error" data-testid="iam-error-forbidden">
                {t("iam.error.forbidden")}
              </span>
            )}
            {isUnavailable && (
              <span role="alert" className="form-error" data-testid="iam-error-unavailable">
                {t("iam.error.unavailable")}
              </span>
            )}
            {isValidation && (
              <span role="alert" className="form-error" data-testid="iam-error-validation">
                {t("iam.error.validation")}
              </span>
            )}
          </div>
        </form>
      </Card>

      {created && <TempPasswordPanel summary={created} onDone={revealDone} />}

      {canRead && me && <StaffList me={me} canElevate={canElevate} />}
    </section>
  );
}

// The staff LIST — org-scoped GET /v1/admin/staff (keyset "daha fazla" pagination).
// Honest loading / error / empty states; each row opens an inline manage panel below
// the table (mirroring the customers surface's list → detail pattern).
function StaffList({ me, canElevate }: { me: Me; canElevate: boolean }) {
  const { t } = useT();
  const list = useStaffList(true);
  const [selected, setSelected] = useState<string | null>(null);

  const rows: StaffSummary[] = list.data?.pages.flatMap((p) => p.data ?? []) ?? [];
  const selectedRow = rows.find((r) => r.id === selected) ?? null;

  return (
    <>
      <TableCard title={t("iam.list.title")} data-testid="iam-list">
        {list.isLoading && <LoadingState label={t("iam.list.loading")} rows={3} />}
        {list.error instanceof ProblemError && (
          <ErrorState title={t("iam.list.error")} description={list.error.problem.detail} />
        )}
        {!list.isLoading && !list.error && rows.length === 0 && (
          <EmptyState data-testid="iam-empty" icon="member" title={t("iam.list.empty")} />
        )}
        {rows.length > 0 && (
          <>
            <Table>
              <thead>
                <tr>
                  <th>{t("iam.col.name")}</th>
                  <th>{t("iam.col.email")}</th>
                  <th>{t("iam.col.role")}</th>
                  <th>{t("iam.col.status")}</th>
                  <th className="cust-td-right" />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} data-testid="iam-row">
                    <td className="cust-strong">{s.displayName}</td>
                    <td>{s.email ?? "—"}</td>
                    <td>{roleLabel(t, s.roleCode)}</td>
                    <td>
                      <StatusLabel tone={s.enabled ? "active" : "ended"}>
                        {s.enabled ? t("iam.status.active") : t("iam.status.passive")}
                      </StatusLabel>
                    </td>
                    <td className="cust-td-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        data-testid="iam-manage-btn"
                        onClick={() => setSelected((cur) => (cur === s.id ? null : s.id))}
                      >
                        {t("iam.manage")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {list.hasNextPage && (
              <div className="k-form-actions">
                <Button
                  variant="secondary"
                  type="button"
                  data-testid="iam-more"
                  loading={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  {t("iam.list.more")}
                </Button>
              </div>
            )}
          </>
        )}
      </TableCard>

      {selectedRow && (
        <StaffManageCard key={selectedRow.id} staff={selectedRow} me={me} canElevate={canElevate} />
      )}
    </>
  );
}

// The per-staffer manage panel: rename + role change (PATCH), enable/disable (POST
// status) and a one-time password reset. RBAC is UX — an ELEVATED target is locked to
// non-super-admins, the elevated role options are hidden without staff.admin:write, and
// the caller can never disable their own account. The API re-enforces every call.
function StaffManageCard({
  staff,
  me,
  canElevate,
}: {
  staff: StaffSummary;
  me: Me;
  canElevate: boolean;
}) {
  const { t } = useT();
  const update = useUpdateStaff(staff.id);
  const status = useSetStaffStatus(staff.id);

  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(staff.displayName);
  const [roleCode, setRoleCode] = useState<AssignableRoleCode>(
    (staff.roleCode as AssignableRoleCode) ?? "ADVISOR",
  );
  const [toast, setToast] = useState<MessageKey | null>(null);

  // A plain admin may not manage an administrator (the elevated target lock, mirrored
  // from the service). Elevated role options are also hidden in the select.
  const targetElevated = staff.roleCode !== null && isElevatedRole(staff.roleCode);
  const locked = targetElevated && !canElevate;
  const self = isSelf(me, staff);

  const roleOptions = ASSIGNABLE_ROLE_CODES.filter((code) => canElevate || !isElevatedRole(code));

  function beginEdit() {
    setDisplayName(staff.displayName);
    setRoleCode((staff.roleCode as AssignableRoleCode) ?? "ADVISOR");
    update.reset();
    setEditing(true);
  }

  const patch: UpdateStaffInput = {};
  if (displayName.trim() !== staff.displayName && displayName.trim() !== "")
    patch.displayName = displayName.trim();
  if (roleCode !== staff.roleCode) patch.roleCode = roleCode;
  const dirty = Object.keys(patch).length > 0;
  const canSave = dirty && displayName.trim() !== "" && !update.isPending;

  function save() {
    update.mutate(patch, {
      onSuccess: () => {
        setEditing(false);
        setToast("iam.edit.success");
      },
    });
  }

  function toggleStatus() {
    status.mutate(!staff.enabled, {
      onSuccess: () => setToast("iam.status.success"),
    });
  }

  const editErrorKey = mutationErrorKey(update.error);
  const statusErrorKey = mutationErrorKey(status.error);

  return (
    <>
      <Card className="cust-detailcard" data-testid="iam-managecard">
        <div className="surf-cardhead">
          <span className="surf-cardtitle">
            <Icon name="member" size={16} strokeWidth={1.9} /> {staff.displayName}
          </span>
          <StatusLabel tone={staff.enabled ? "active" : "ended"}>
            {staff.enabled ? t("iam.status.active") : t("iam.status.passive")}
          </StatusLabel>
        </div>

        {locked ? (
          <p className="muted surf-note" data-testid="iam-manage-locked">
            {t("iam.manage.elevatedLocked")}
          </p>
        ) : (
          <>
            <form
              data-testid="iam-edit-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (canSave) save();
              }}
            >
              <fieldset className="k-form-grid" disabled={!editing}>
                <Input
                  label={t("iam.field.displayName")}
                  value={editing ? displayName : staff.displayName}
                  readOnly={!editing}
                  onChange={(e) => setDisplayName(e.target.value)}
                  data-testid="iam-edit-name"
                />
                <Select
                  label={t("iam.field.role")}
                  value={editing ? roleCode : ((staff.roleCode as AssignableRoleCode) ?? "ADVISOR")}
                  disabled={!editing}
                  onChange={(e) => setRoleCode(e.target.value as AssignableRoleCode)}
                  hint={canElevate ? undefined : t("iam.role.elevatedHidden")}
                  data-testid="iam-edit-role"
                >
                  {roleOptions.map((code) => (
                    <option key={code} value={code}>
                      {t(ROLE_LABEL_KEY[code])}
                    </option>
                  ))}
                </Select>
              </fieldset>

              <div className="k-form-actions">
                {!editing && (
                  <Button
                    variant="secondary"
                    type="button"
                    data-testid="iam-edit-btn"
                    onClick={beginEdit}
                  >
                    {t("iam.edit")}
                  </Button>
                )}
                {editing && (
                  <>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={!canSave}
                      loading={update.isPending}
                      data-testid="iam-edit-save"
                    >
                      {t("iam.edit.save")}
                    </Button>
                    <Button
                      variant="ghost"
                      type="button"
                      onClick={() => setEditing(false)}
                      disabled={update.isPending}
                    >
                      {t("iam.edit.cancel")}
                    </Button>
                  </>
                )}
                {editErrorKey && (
                  <span role="alert" className="form-error" data-testid="iam-edit-error">
                    {t(editErrorKey)}
                  </span>
                )}
              </div>
            </form>

            <div className="cust-reset">
              <div className="cust-reset__head">
                <span className="surf-cardtitle">{t("iam.status.title")}</span>
              </div>
              <div className="k-form-actions">
                {self && staff.enabled ? (
                  <span className="muted surf-note" data-testid="iam-status-self">
                    {t("iam.status.selfNote")}
                  </span>
                ) : (
                  <Button
                    variant={staff.enabled ? "secondary" : "primary"}
                    type="button"
                    data-testid="iam-status-toggle"
                    loading={status.isPending}
                    onClick={toggleStatus}
                  >
                    {staff.enabled ? t("iam.status.disable") : t("iam.status.enable")}
                  </Button>
                )}
                {statusErrorKey && (
                  <span role="alert" className="form-error" data-testid="iam-status-error">
                    {t(statusErrorKey)}
                  </span>
                )}
              </div>
            </div>

            <ResetStaffPassword id={staff.id} />
          </>
        )}
      </Card>

      {toast && (
        <ToastStack>
          <Toast
            tone="success"
            title={t(toast)}
            closeLabel={t("iam.toast.dismiss")}
            onClose={() => setToast(null)}
          />
        </ToastStack>
      )}
    </>
  );
}

// Password reset — a one-time temporary password. A calm confirm, then the value is
// revealed ONCE in the same reveal chrome as the create surface (copy affordance, an
// unmistakable "shown once" caption) and is never cached.
function ResetStaffPassword({ id }: { id: string }) {
  const { t } = useT();
  const reset = useResetStaffPassword(id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  function confirmReset() {
    reset.mutate(undefined, {
      onSuccess: (d) => {
        setConfirmOpen(false);
        setTempPassword(d.temporaryPassword);
      },
      onError: () => setConfirmOpen(false),
    });
  }

  function closeReveal() {
    setTempPassword(null);
    reset.reset();
  }

  const errorKey = mutationErrorKey(reset.error);

  return (
    <div className="cust-reset" data-testid="iam-reset">
      <div className="cust-reset__head">
        <span className="surf-cardtitle">{t("iam.reset.title")}</span>
      </div>
      <p className="muted surf-note">{t("iam.reset.note")}</p>

      <div className="k-form-actions">
        <Button
          variant="danger"
          type="button"
          data-testid="iam-reset-btn"
          onClick={() => setConfirmOpen(true)}
        >
          {t("iam.reset.action")}
        </Button>
        {errorKey && (
          <span role="alert" className="form-error" data-testid="iam-reset-error">
            {t(errorKey)}
          </span>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        tone="danger"
        icon="lock"
        title={t("iam.reset.confirmTitle")}
        description={t("iam.reset.confirmBody")}
        cancelLabel={t("iam.edit.cancel")}
        confirmLabel={t("iam.reset.action")}
        confirming={reset.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmReset}
      />

      {tempPassword && (
        <Card className="iam-reveal" data-testid="iam-reset-reveal">
          <div className="iam-reveal__vault">
            <Icon name="vault" size={18} strokeWidth={1.9} />
            <span className="surf-cardtitle">{t("iam.reveal.title")}</span>
          </div>
          <p className="muted surf-note">{t("iam.reveal.note")}</p>
          <div className="cust-temp">
            <code className="cust-temp__value" data-testid="iam-reset-temp-value">
              {tempPassword}
            </code>
            <ResetCopyButton value={tempPassword} />
          </div>
          <div className="k-form-actions">
            <Button
              variant="primary"
              type="button"
              onClick={closeReveal}
              data-testid="iam-reset-done"
            >
              {t("iam.reveal.done")}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function ResetCopyButton({ value }: { value: string }) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard unavailable (permissions / non-secure context) — the value is still
      // visible for manual transcription.
    }
  }
  return (
    <Button variant="secondary" size="sm" type="button" onClick={() => void copy()}>
      {copied ? t("iam.reveal.copied") : t("iam.reveal.copy")}
    </Button>
  );
}

// The one careful sensitive reveal — a calm panel (not a modal): the created identity plus
// the one-time temporary password with a copy affordance and an unmistakable "shown once,
// convey securely" caption. Mirrors the customers surface's reset reveal; the value is
// never cached (it lives only in `created` state until dismissed).
function TempPasswordPanel({
  summary,
  onDone,
}: {
  summary: StaffCreatedSummary;
  onDone: () => void;
}) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const roleLabelKey = ROLE_LABEL_KEY[summary.roleCode as AssignableRoleCode];

  async function copy() {
    try {
      await navigator.clipboard?.writeText(summary.temporaryPassword);
      setCopied(true);
    } catch {
      // Clipboard unavailable (permissions / non-secure context) — the value is still
      // visible for manual transcription.
    }
  }

  return (
    <Card className="iam-reveal" data-testid="iam-created">
      <div className="k-note__row" style={{ alignItems: "center" }}>
        <div className="k-note__icon k-note__icon--success">
          <Icon name="check-circle" size={21} strokeWidth={2} />
        </div>
        <div>
          <div className="surf-cardtitle">{t("iam.created.title")}</div>
          <div className="muted surf-note" data-testid="iam-created-who">
            {summary.displayName} · {summary.email} ·{" "}
            {roleLabelKey ? t(roleLabelKey) : summary.roleCode}
          </div>
        </div>
      </div>

      <div className="iam-reveal__vault">
        <Icon name="vault" size={18} strokeWidth={1.9} />
        <span className="surf-cardtitle">{t("iam.reveal.title")}</span>
      </div>
      <p className="muted surf-note">{t("iam.reveal.note")}</p>

      <div className="cust-temp">
        <code className="cust-temp__value" data-testid="iam-temp-value">
          {summary.temporaryPassword}
        </code>
        <Button variant="secondary" size="sm" type="button" onClick={() => void copy()}>
          {copied ? t("iam.reveal.copied") : t("iam.reveal.copy")}
        </Button>
      </div>

      <div className="k-form-actions">
        <Button variant="primary" type="button" onClick={onDone} data-testid="iam-reveal-done">
          {t("iam.reveal.done")}
        </Button>
      </div>
    </Card>
  );
}
